/**
 * Intelligence Feed Component
 * AI-powered insights and actionable intelligence
 */

import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { ScrollArea } from '../ui/scroll-area'
import { cn } from '../../lib/utils'
import { 
  Brain, Sparkles, TrendingUp, AlertTriangle, Info,
  ArrowRight, CheckCircle, XCircle, Clock, Zap,
  Target, Users, BarChart3, RefreshCw, Filter,
  ChevronRight, ExternalLink, MessageSquare
} from 'lucide-react'

interface Insight {
  id: string
  type: string
  priority: string
  title: string
  content: string
  actionUrl?: string
  actionLabel?: string
  metadata: any
  createdAt?: Date
}

interface Intelligence {
  patterns?: Array<{
    type: string
    description: string
    confidence: number
  }>
  predictions?: Array<{
    event: string
    probability: number
    timeframe: string
  }>
  suggestions?: Array<{
    title: string
    reason: string
    impact: string
  }>
  risks?: Array<{
    title: string
    severity: string
    mitigation: string
  }>
}

interface IntelligenceFeedProps {
  insights: Insight[]
  intelligence?: Intelligence
  onActionClick?: (insight: Insight) => void
  onDismiss?: (insightId: string) => void
  minimal?: boolean
  sidebar?: boolean
}

function InsightCard({ 
  insight, 
  onAction, 
  onDismiss 
}: { 
  insight: Insight
  onAction?: () => void
  onDismiss?: () => void
}) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'suggestion': return <Sparkles className="w-4 h-4 text-blue-500" />
      case 'warning': return <AlertTriangle className="w-4 h-4 text-orange-500" />
      case 'achievement': return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'trend': return <TrendingUp className="w-4 h-4 text-purple-500" />
      case 'deadline': return <Clock className="w-4 h-4 text-red-500" />
      case 'collaboration': return <Users className="w-4 h-4 text-indigo-500" />
      default: return <Info className="w-4 h-4 text-gray-500" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'border-red-300 bg-red-50'
      case 'high': return 'border-orange-300 bg-orange-50'
      case 'medium': return 'border-blue-300 bg-blue-50'
      case 'low': return 'border-gray-300 bg-gray-50'
      default: return 'border-gray-200 bg-white'
    }
  }

  const formatTimeAgo = (date?: Date) => {
    if (!date) return 'Just now'
    const minutes = Math.floor((new Date().getTime() - new Date(date).getTime()) / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  return (
    <div className={cn(
      "group relative border rounded-lg p-4 transition-all duration-200",
      "hover:shadow-md hover:border-krushr-primary/50",
      getPriorityColor(insight.priority)
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {getTypeIcon(insight.type)}
          <span className="text-xs text-krushr-gray-500">
            {formatTimeAgo(insight.createdAt)}
          </span>
        </div>
        
        {onDismiss && (
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation()
              onDismiss()
            }}
          >
            <XCircle className="w-3 h-3" />
          </Button>
        )}
      </div>

      {/* Content */}
      <h4 className="font-medium text-sm text-krushr-gray-900 mb-1">
        {insight.title}
      </h4>
      <p className="text-xs text-krushr-gray-600 line-clamp-2 mb-3">
        {insight.content}
      </p>

      {/* Action */}
      {insight.actionUrl && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs text-krushr-primary hover:text-krushr-primary-600"
          onClick={onAction}
        >
          {insight.actionLabel || 'View'}
          <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )}

      {/* Metadata badges */}
      {insight.metadata && (
        <div className="flex items-center gap-2 mt-2">
          {insight.metadata.taskCount && (
            <Badge variant="outline" className="text-xs">
              {insight.metadata.taskCount} tasks
            </Badge>
          )}
          {insight.metadata.impact && (
            <Badge variant="outline" className="text-xs">
              {insight.metadata.impact} impact
            </Badge>
          )}
          {insight.metadata.confidence && (
            <Badge variant="outline" className="text-xs">
              {Math.round(insight.metadata.confidence * 100)}% sure
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}

function IntelligencePanel({ intelligence }: { intelligence: Intelligence }) {
  const [activeTab, setActiveTab] = useState<'patterns' | 'predictions' | 'suggestions' | 'risks'>('patterns')

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Brain className="w-4 h-4 text-krushr-primary" />
            AI Analysis
          </CardTitle>
          <div className="flex items-center gap-1">
            {(['patterns', 'predictions', 'suggestions', 'risks'] as const).map((tab) => (
              <Button
                key={tab}
                size="sm"
                variant={activeTab === tab ? 'default' : 'ghost'}
                className="h-6 px-2 text-xs capitalize"
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {activeTab === 'patterns' && intelligence.patterns && (
          <div className="space-y-2">
            {intelligence.patterns.map((pattern, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <BarChart3 className="w-3 h-3 text-krushr-primary mt-0.5" />
                <div className="flex-1">
                  <p className="text-krushr-gray-700">{pattern.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs h-4">
                      {pattern.type}
                    </Badge>
                    <span className="text-krushr-gray-500">
                      {Math.round(pattern.confidence * 100)}% confidence
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'predictions' && intelligence.predictions && (
          <div className="space-y-2">
            {intelligence.predictions.map((prediction, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <Target className="w-3 h-3 text-purple-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-krushr-gray-700">{prediction.event}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs h-4">
                      {Math.round(prediction.probability * 100)}%
                    </Badge>
                    <span className="text-krushr-gray-500">
                      {prediction.timeframe}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'suggestions' && intelligence.suggestions && (
          <div className="space-y-2">
            {intelligence.suggestions.map((suggestion, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <Sparkles className="w-3 h-3 text-blue-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-krushr-gray-700">{suggestion.title}</p>
                  <p className="text-krushr-gray-600 mt-0.5">{suggestion.reason}</p>
                  <Badge variant="outline" className="text-xs h-4 mt-1">
                    {suggestion.impact}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'risks' && intelligence.risks && (
          <div className="space-y-2">
            {intelligence.risks.map((risk, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <AlertTriangle className={cn(
                  "w-3 h-3 mt-0.5",
                  risk.severity === 'high' ? 'text-red-500' : 
                  risk.severity === 'medium' ? 'text-orange-500' : 'text-yellow-500'
                )} />
                <div className="flex-1">
                  <p className="font-medium text-krushr-gray-700">{risk.title}</p>
                  <p className="text-krushr-gray-600 mt-0.5">{risk.mitigation}</p>
                  <Badge 
                    variant={risk.severity === 'high' ? 'destructive' : 'outline'} 
                    className="text-xs h-4 mt-1"
                  >
                    {risk.severity}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function IntelligenceFeed({
  insights,
  intelligence,
  onActionClick,
  onDismiss,
  minimal = false,
  sidebar = false
}: IntelligenceFeedProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  
  if (minimal) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Brain className="w-4 h-4" />
          Intelligence ({insights.length})
        </h3>
        <div className="space-y-2">
          {insights.slice(0, 3).map((insight) => (
            <InsightCard
              key={insight.id}
              insight={insight}
              onAction={() => onActionClick?.(insight)}
              onDismiss={onDismiss ? () => onDismiss(insight.id) : undefined}
            />
          ))}
        </div>
      </div>
    )
  }

  if (sidebar) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div 
          className="px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <Brain className="w-4 h-4 text-pink-500" />
              Insights
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{insights.length}</span>
              <ChevronRight className={cn(
                "w-4 h-4 text-gray-400 transition-transform",
                isExpanded && "rotate-90"
              )} />
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4">
            <ScrollArea className="h-64">
              {insights.length > 0 ? (
                <div className="space-y-3">
                  {insights.slice(0, 5).map((insight) => (
                    <div
                      key={insight.id}
                      className="p-3 border border-gray-200 rounded-lg hover:border-pink-200 transition-colors cursor-pointer"
                      onClick={() => onActionClick?.(insight)}
                    >
                      <div className="flex items-start gap-2">
                        {insight.type === 'suggestion' && <Sparkles className="w-3 h-3 text-blue-500 mt-0.5" />}
                        {insight.type === 'warning' && <AlertTriangle className="w-3 h-3 text-orange-500 mt-0.5" />}
                        {insight.type === 'achievement' && <CheckCircle className="w-3 h-3 text-green-500 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 line-clamp-2">
                            {insight.title}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                            {insight.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Brain className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No insights yet</p>
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </div>
    )
  }

  const [filter, setFilter] = useState<string>('all')
  
  const filteredInsights = useMemo(() => {
    if (filter === 'all') return insights
    return insights.filter(i => i.type === filter)
  }, [insights, filter])

  const insightTypes = useMemo(() => {
    const types = new Set(insights.map(i => i.type))
    return ['all', ...Array.from(types)]
  }, [insights])

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Brain className="w-5 h-5 text-pink-500" />
          Intelligence Feed
        </h3>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filter
          </Button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {insightTypes.map((type) => (
          <Button
            key={type}
            size="sm"
            variant={filter === type ? 'default' : 'outline'}
            className="h-7 px-3 text-xs capitalize"
            onClick={() => setFilter(type)}
          >
            {type}
            {type !== 'all' && (
              <Badge variant="secondary" className="ml-1 h-4 px-1">
                {insights.filter(i => i.type === type).length}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Insights Feed */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3 bg-gray-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">
              Latest Insights
            </CardTitle>
            <Badge variant="secondary" className="bg-white/50">
              {filteredInsights.length} active
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="p-3">
          <ScrollArea className="h-[400px] pr-3">
            {filteredInsights.length > 0 ? (
              <div className="space-y-3">
                {filteredInsights.map((insight) => (
                  <InsightCard
                    key={insight.id}
                    insight={insight}
                    onAction={() => onActionClick?.(insight)}
                    onDismiss={onDismiss ? () => onDismiss(insight.id) : undefined}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-center">
                <Brain className="w-8 h-8 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">
                  No insights available yet
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  AI is analyzing your patterns...
                </p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* AI Intelligence Panel */}
      {intelligence && (
        <IntelligencePanel intelligence={intelligence} />
      )}
    </div>
  )
}